import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";

interface PrescriptionItem {
  id?: string;
  item_date?: string;
  item_type: string;
  name: string;
  usage_route?: string;
  concentration?: string;
  dosage?: string;
  duration?: string;
  observations?: string;
  acquisition?: string;
}

interface PrescriptionTableProps {
  items: PrescriptionItem[];
  onItemsChange: (items: PrescriptionItem[]) => void;
}

const itemTypeLabels: Record<string, string> = {
  medication: "Medicamento",
  supplement: "Suplemento",
  vitamin: "Vitamina",
  other: "Outro",
};

const usageRouteLabels: Record<string, string> = {
  oral: "Oral",
  topical: "Tópico",
  injectable: "Injetável",
  inhalatory: "Inalatório",
};

const itemTypeIcons: Record<string, string> = {
  medication: "💊",
  supplement: "🌿",
  vitamin: "💛",
  other: "📦",
};

// Função para calcular quantidade de comprimidos
const calculatePillQuantity = (dosage?: string, duration?: string): string => {
  if (!dosage || !duration) return "";
  
  // Extrair número de comprimidos da dosagem
  const dosageMatch = dosage.match(/(\d+)\s*(comprimido|comp|cp)/i);
  const frequencyMatch = dosage.match(/(\d+)\s*(?:vez|x|\/)/i) || dosage.match(/(\d+)\/\d+h/);
  
  let pillsPerDay = 1;
  if (dosageMatch) {
    pillsPerDay = parseInt(dosageMatch[1]);
    if (frequencyMatch) {
      pillsPerDay *= parseInt(frequencyMatch[1]);
    }
  }
  
  // Extrair duração em dias
  let days = 30;
  if (duration.toLowerCase().includes("contínuo") || duration.toLowerCase().includes("continuo")) {
    days = 30;
  } else {
    const durationMatch = duration.match(/(\d+)\s*(dia|semana|mês|mes)/i);
    if (durationMatch) {
      const num = parseInt(durationMatch[1]);
      const unit = durationMatch[2].toLowerCase();
      if (unit.startsWith("semana")) days = num * 7;
      else if (unit.startsWith("m")) days = num * 30;
      else days = num;
    }
  }
  
  const totalPills = pillsPerDay * days;
  const maxPillsPerBox = 30;
  const boxes = Math.ceil(totalPills / maxPillsPerBox);
  
  if (boxes === 1) {
    return `1 caixa com ${Math.min(totalPills, maxPillsPerBox)} comprimidos`;
  }
  return `${boxes} caixas com ${maxPillsPerBox} comprimidos cada`;
};

export function PrescriptionTable({ items, onItemsChange }: PrescriptionTableProps) {
  // Auto-preencher aquisição quando dosagem e duração mudarem
  useEffect(() => {
    let hasChanges = false;
    const updatedItems = items.map(item => {
      if (item.dosage && item.duration && !item.acquisition) {
        const suggested = calculatePillQuantity(item.dosage, item.duration);
        if (suggested) {
          hasChanges = true;
          return { ...item, acquisition: suggested };
        }
      }
      return item;
    });
    
    if (hasChanges) {
      onItemsChange(updatedItems);
    }
  }, [items.map(i => `${i.dosage}-${i.duration}-${i.acquisition}`).join(',')]);

  const updateItem = (index: number, field: keyof PrescriptionItem, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onItemsChange(newItems);
  };

  const removeItem = (index: number) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const addEmptyItem = () => {
    const newItem: PrescriptionItem = {
      item_date: format(new Date(), "yyyy-MM-dd"),
      item_type: "medication",
      name: "",
      usage_route: "oral",
      concentration: "",
      dosage: "",
      duration: "",
      observations: "",
      acquisition: "",
    };
    onItemsChange([...items, newItem]);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {items.map((item, index) => (
          <Card key={index} className="p-4">
            {/* Header com nome em destaque */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-xl flex-shrink-0">{itemTypeIcons[item.item_type] || "💊"}</span>
                <Input
                  value={item.name}
                  onChange={(e) => updateItem(index, "name", e.target.value)}
                  placeholder="Nome do medicamento"
                  className="font-medium border-0 bg-transparent px-0 h-auto text-base focus-visible:ring-0 shadow-none"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeItem(index)}
                className="flex-shrink-0 h-9 w-9 p-0 hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Campos organizados em grid responsivo */}
            <div className="space-y-3">
              {/* Linha 1: Tipo, Via de Uso, Concentração, Data */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Tipo</label>
                  <Select
                    value={item.item_type}
                    onValueChange={(value) => updateItem(index, "item_type", value)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(itemTypeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Via de Uso</label>
                  <Select
                    value={item.usage_route || "oral"}
                    onValueChange={(value) => updateItem(index, "usage_route", value)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(usageRouteLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Concentração</label>
                  <Input
                    value={item.concentration || ""}
                    onChange={(e) => updateItem(index, "concentration", e.target.value)}
                    placeholder="Ex: 5mg"
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Data</label>
                  <Input
                    type="date"
                    value={item.item_date || ""}
                    onChange={(e) => updateItem(index, "item_date", e.target.value)}
                    className="h-9"
                  />
                </div>
              </div>

              {/* Linha 2: Dosagem e Prazo */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Dosagem</label>
                  <Input
                    value={item.dosage || ""}
                    onChange={(e) => updateItem(index, "dosage", e.target.value)}
                    placeholder="Ex: 1 comprimido 2x ao dia"
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Prazo</label>
                  <Input
                    value={item.duration || ""}
                    onChange={(e) => updateItem(index, "duration", e.target.value)}
                    placeholder="Ex: 30 dias"
                    className="h-9"
                  />
                </div>
              </div>

              {/* Linha 3: Aquisição e Observações */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Aquisição</label>
                  <Input
                    value={item.acquisition || ""}
                    onChange={(e) => updateItem(index, "acquisition", e.target.value)}
                    placeholder="Ex: 1 caixa com 30 comprimidos"
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Observações</label>
                  <Input
                    value={item.observations || ""}
                    onChange={(e) => updateItem(index, "observations", e.target.value)}
                    placeholder="Ex: Tomar após refeições"
                    className="h-9"
                  />
                </div>
              </div>
            </div>
          </Card>
        ))}

        {items.length === 0 && (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
            Nenhum item adicionado. Use a captura multimodal ou clique em "Adicionar Item Manual".
          </div>
        )}
      </div>

      <Button
        variant="outline"
        onClick={addEmptyItem}
        className="w-full border-dashed border-2 hover:border-teal-400 hover:bg-teal-50"
      >
        <Plus className="h-4 w-4 mr-2" />
        Adicionar Item Manual
      </Button>
    </div>
  );
}
